<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useArrowsAiAssistants } from 'dashboard/composables/useArrowsAiAssistants';
import SettingsPageLayout from 'dashboard/components-next/arrowsAi/settings/SettingsPageLayout.vue';
import ControlItems from 'dashboard/components-next/arrowsAi/settings/ControlItems.vue';
import MissingAssistant from 'dashboard/components-next/arrowsAi/MissingAssistant.vue';

const { t } = useI18n();
const route = useRoute();
const { getAssistant, rememberAssistant, updateAssistant } =
  useArrowsAiAssistants();

const assistant = computed(() => {
  const current = getAssistant(route.params.assistantId);
  if (current) rememberAssistant(current.id);
  return current;
});

const items = computed(() => assistant.value?.replyStyles || []);
const examples = computed(() => [
  t('ARROWS_AI.SETTINGS.REPLY_STYLE.EXAMPLE_1'),
  t('ARROWS_AI.SETTINGS.REPLY_STYLE.EXAMPLE_2'),
  t('ARROWS_AI.SETTINGS.REPLY_STYLE.EXAMPLE_3'),
]);

const save = async next => {
  if (!assistant.value) return;
  await updateAssistant(assistant.value.id, { replyStyles: next });
};

const add = async content => {
  if (items.value.includes(content)) return;
  try {
    await save([...items.value, content]);
    useAlert(t('ARROWS_AI.SETTINGS.REPLY_STYLE.ADDED'));
  } catch (error) {
    useAlert(error.message || t('ARROWS_AI.SETTINGS.ERROR'));
  }
};

const addAll = async () => {
  try {
    await save([...new Set([...items.value, ...examples.value])]);
    useAlert(t('ARROWS_AI.SETTINGS.REPLY_STYLE.ADDED'));
  } catch (error) {
    useAlert(error.message || t('ARROWS_AI.SETTINGS.ERROR'));
  }
};

const remove = async index => {
  try {
    await save(items.value.filter((_, itemIndex) => itemIndex !== index));
    useAlert(t('ARROWS_AI.SETTINGS.REPLY_STYLE.DELETED'));
  } catch (error) {
    useAlert(error.message || t('ARROWS_AI.SETTINGS.ERROR'));
  }
};
</script>

<template>
  <SettingsPageLayout
    :heading="t('ARROWS_AI.SETTINGS.REPLY_STYLE.TITLE')"
    :description="t('ARROWS_AI.SETTINGS.REPLY_STYLE.DESCRIPTION')"
  >
    <MissingAssistant v-if="!assistant" />
    <ControlItems
      v-else
      :items="items"
      :examples="examples"
      :suggested-title="t('ARROWS_AI.SETTINGS.REPLY_STYLE.SUGGESTED_TITLE')"
      :add-all-label="t('ARROWS_AI.SETTINGS.REPLY_STYLE.ADD_ALL')"
      :add-this-label="t('ARROWS_AI.SETTINGS.REPLY_STYLE.ADD_THIS')"
      :add-button-label="t('ARROWS_AI.SETTINGS.REPLY_STYLE.ADD')"
      :empty-message="t('ARROWS_AI.SETTINGS.REPLY_STYLE.EMPTY')"
      :input-placeholder="t('ARROWS_AI.SETTINGS.REPLY_STYLE.PLACEHOLDER')"
      :save-label="t('ARROWS_AI.SETTINGS.REPLY_STYLE.SAVE')"
      @add="add"
      @add-all="addAll"
      @remove="remove"
    />
  </SettingsPageLayout>
</template>
